import { SessionRecovery } from './SessionRecovery';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { LoadingStatus } from '../ui/LoadingFeedback';
import { useEffect, useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { AppRole, Permission } from '../../lib/permissions';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'admin' | 'publisher';
  requiredAppRole?: AppRole | readonly AppRole[];
  requiredPermissions?: Permission[];
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole = 'admin',
  requiredAppRole,
  requiredPermissions = [],
}) => {
  const {
    isAuthenticated,
    isLoading,
    user,
    isTokenValid,
    refreshTokens,
    hasAnyPermission,
    sessionError,
  } = useAuth();
  const { t } = useI18n();
  const location = useLocation();
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Check token validity and refresh if needed
  useEffect(() => {
    const checkAndRefreshToken = async () => {
      if (isAuthenticated && user && !sessionError && !isTokenValid()) {
        setIsRefreshing(true);
        try {
          await refreshTokens();
        } catch (error) {
          console.error('Token refresh failed in ProtectedRoute:', error);
          // AuthContext distinguishes expired sessions from temporary failures.
        } finally {
          setIsRefreshing(false);
        }
      }
    };

    // Only check if we have a user and token
    if (isAuthenticated && user) {
      checkAndRefreshToken();
    }
  }, [isAuthenticated, isTokenValid, refreshTokens, user, sessionError]);

  // Show loading spinner while initializing or refreshing
  if (isLoading || isRefreshing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <LoadingStatus label={isRefreshing ? t('protected.refreshing') : t('common.loading')} />
        </div>
      </div>
    );
  }

  if (sessionError) return <SessionRecovery />;

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role-based access
  if (user && requiredRole && user.role !== requiredRole) {
    const roleDisplayName = requiredRole === 'admin' ? t('role.admin') : t('role.publisher');

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-red-100">
            <svg
              className="h-6 w-6 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h2 className="mt-6 text-xl font-semibold text-gray-900">
            {t('protected.accessDenied')}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {t('protected.requiresRole', {
              role: roleDisplayName,
              currentRole: user.role === 'admin' ? t('role.admin') : t('role.publisher'),
            })}
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={() => window.history.back()} className="btn-secondary">
              {t('protected.goBack')}
            </button>
            <Navigate to="/dashboard" replace />
          </div>
        </div>
      </div>
    );
  }

  const allowedAppRoles = typeof requiredAppRole === 'string' ? [requiredAppRole] : requiredAppRole;
  if (allowedAppRoles && (!user?.appRole || !allowedAppRoles.includes(user.appRole))) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <h2 className="text-xl font-semibold text-gray-900">{t('protected.accessDenied')}</h2>
          <p className="mt-2 text-sm text-gray-600">{t('protected.permissionDenied')}</p>
        </div>
      </div>
    );
  }

  if (requiredPermissions.length > 0 && !hasAnyPermission(requiredPermissions)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-red-100">
            <svg
              className="h-6 w-6 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h2 className="mt-6 text-xl font-semibold text-gray-900">
            {t('protected.accessDenied')}
          </h2>
          <p className="mt-2 text-sm text-gray-600">{t('protected.permissionDenied')}</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
